// The "delivered price" model on the web (owner, 2026-10-29). The same math
// as supabase/functions/market-checkout/delivered.ts — the server re-prices
// every order with that file; both are tested on the same numbers
// (marketDelivered.test.js / delivered.test.ts).
//
//   air unit = factory unit + unit chargeable kg × air rate (by class) + fee
//   sea unit = factory unit + unit m³ × sea rate + fee
// Product data (air class, unit kg/cm³, sea offered) comes from the
// market_delivered RPC; the rates and dates from market_public_state().delivered.

import { useEffect, useState } from 'react';
import { marketDb } from './marketDb';
import { cleanTiers, pricingMode } from './marketPricing';

const r2 = (n) => Math.round(n * 100) / 100;

export function airRate(cls, s) {
  return Number(cls === 'sensitive' ? s.air_rate_sensitive : cls === 'electrical' ? s.air_rate_electrical : s.air_rate_general);
}

/** Sea by class (owner, 2026-11-02: partner cost + 10% — 1,320 / 1,540 / 1,760); one rate until the class rates are set. */
export function seaRate(cls, s) {
  const by = cls === 'sensitive' ? s.sea_rate_sensitive : cls === 'electrical' ? s.sea_rate_electrical : null;
  return Number(by) > 0 ? Number(by) : Number(s.sea_rate_cbm);
}

/**
 * Price rounding (owner, 2026-11-02), always UP: x.00 is kept; under 10 → the next
 * 0.05; 10–100 → the next .45 or .95 (99.96–99.99 → 100.95); above 100 → the next .95.
 * Server twin: market-checkout/delivered.ts roundPrice; app twin: marketDeliveredCore.js.
 */
export function roundPrice(p) {
  const c = Math.round(Number(p) * 100);
  if (!(c > 0) || c % 100 === 0) return r2(p);
  if (c < 1000) return Math.ceil(c / 5) * 5 / 100;
  const n = Math.floor(c / 100), f = c - n * 100;
  if (c <= 10000) return f <= 45 ? r2(n + 0.45) : f <= 95 ? r2(n + 0.95) : n + 1 < 100 ? r2(n + 1.45) : 100.95;
  return f <= 95 ? r2(n + 0.95) : r2(n + 1.95);
}
/** A shown unit price: rounded up when the store's rounding is on (market_settings.price_rounding_enabled), else to the halala. */
export const shownPrice = (p, s) => (s?.price_rounding ? roundPrice(p) : r2(p));
/** The payment allowance (owner, 2026-11-02: 1.5% of the goods, never shown — Telr charges on the whole cart). */
export const allowanceRate = (s) => Number(s?.payment_allowance) || 0;
/** The product price without shipping: goods + fee + allowance, rounded as shown («السعر بدون الشحن»). */
export function productPrice(unitSar, s) {
  const u = Number(unitSar);
  return shownPrice(u + u * Number(s.fee_rate) + u * allowanceRate(s), s);
}

/** Per unit: chargeable kg by air, m³ by sea; the bulk and nesting rules, and sea on weight too (delivered.ts, line for line). */
export function unitMeasures(sku, qty, s) {
  const kg = Number(sku.kg), cm3 = Number(sku.cm3);
  const pf = Number(s.packaging_factor), div = Number(s.volumetric_divisor);
  const ratio = kg > 0 ? (cm3 / div) / (kg * pf) : Infinity;
  const atBulkQty = qty >= Number(s.bulk_min_qty);
  const bulk = atBulkQty && ratio <= Number(s.bulk_max_ratio);
  const nf = Number(s.nesting_factor);
  const nest = !bulk && atBulkQty && sku.nests === true && nf > 0;
  const vf = bulk ? Number(s.bulk_volume_factor) : nest ? nf : 1;
  const kpc = Number(s.sea_kg_per_cbm);
  return { air_kg: Math.max(kg * pf, (cm3 / div) * vf), sea_cbm: Math.max((cm3 / 1e6) * vf, kpc > 0 ? (kg * pf) / kpc : 0), bulk, nest };
}

/**
 * Delivered unit prices (SAR) of one variant at a quantity; sea is null when sea is not
 * offered, air is null when the variant is sea-only (sku.air_ok === false — bulky goods,
 * owner 2026-11-02 §1.5). Fee and payment allowance are folded in, then rounded as shown.
 */
export function deliveredUnit(unitSar, sku, cls, qty, s) {
  const u = unitMeasures(sku, qty, s);
  const fee = unitSar * Number(s.fee_rate) + unitSar * allowanceRate(s);
  return {
    air: sku.air_ok === false ? null : shownPrice(unitSar + u.air_kg * airRate(cls, s) + fee, s),
    sea: sku.sea_ok ? shownPrice(unitSar + u.sea_cbm * seaRate(cls, s) + fee, s) : null,
    air_kg: u.air_kg, sea_cbm: u.sea_cbm, bulk: u.bulk,
  };
}

/**
 * Rule B (owner, 2026-10-30): air is unsuited to a variant when, for ONE piece
 * (qty 1, no bulk factor), its air freight is more than half of its delivered
 * air unit price AND its chargeable kg is more than 30. The card and the
 * product page then show sea only; air stays at checkout, listed last.
 * This is the air test alone: callers apply it only where sea is offered
 * (sku.sea_ok) — with no sea, nothing is hidden (a later rule handles those).
 * Same name and math as the app's src/lib/market/marketDeliveredCore.js.
 */
export function airUnsuited(sku, cls, unitSar, s) {
  if (sku?.air_ok === false) return true;   // sea-only goods (bulky, §1.5): air never suits them
  if (!sku || !(Number(unitSar) > 0)) return false;
  const u = unitMeasures(sku, 1, s);
  const freight = u.air_kg * airRate(cls, s);
  const air = deliveredUnit(Number(unitSar), sku, cls, 1, s).air;
  return u.air_kg > 30 && freight > 0.5 * air;
}

// ── The tiered service fee (owner, 2026-10-31) ─────────────────────────────
// s.fee_tiers: [{ from_sar, rate }] ascending (market_fee_schedule), s.fee_rate its first
// (highest) rate. Without tiers, the flat s.fee_rate. The SERVER decides the rate it charges
// (market_fee_rate) with the same ladder; this is how the cart shows it beforehand.
export const feeLadder = (s) => (Array.isArray(s?.fee_tiers) && s.fee_tiers.length ? s.fee_tiers : [{ from_sar: 0, rate: Number(s?.fee_rate) || 0 }]);
/** The rate for a cart whose base (cartFeeBase: its delivered total before the tier discount) is `base` SAR. */
export function cartFeeRate(base, s) {
  let rate = feeLadder(s)[0].rate;
  for (const t of feeLadder(s)) if (base >= t.from_sar) rate = t.rate;
  return Number(rate);
}
/** The next step down the ladder: how much more (in delivered prices), and its rate — or null at the bottom. */
export function nextFeeTier(base, s) {
  const t = feeLadder(s).find((x) => x.from_sar > base);
  return t ? { from_sar: t.from_sar, rate: Number(t.rate), need_sar: r2(t.from_sar - base) } : null;
}
export const lowestFeeRate = (s) => Number(feeLadder(s)[feeLadder(s).length - 1].rate);
/**
 * Advertise a lower price only when it is worth it (owner, 2026-10-31): at least
 * s.advertise_min_saving (3%) below the price shown. Below that the card shows the price
 * alone and the product page no tier table — the cart discount still applies as normal.
 */
export const worthAdvertising = (low, shown, s) => low != null && shown > 0
  && low <= shown * (1 - (Number.isFinite(Number(s?.advertise_min_saving)) ? Number(s.advertise_min_saving) : 0.03)) + 1e-9;

/**
 * Rule C (owner, 2026-10-30/31): air does not suit the variant (rule B) and it has no
 * reliable sea price — its shipping price is confirmed by staff first. The card and page
 * show the product price (goods + fee) and «نثبّت لك سعر الشحن خلال 48 ساعة»; a cart
 * holding one waits as a whole (market-checkout 'confirm'). Server twin:
 * market-checkout/handler.ts confirmFirstLines.
 */
export function confirmFirst(sku, cls, unitSar, s) {
  return !!sku && !sku.sea_ok && airUnsuited(sku, cls, unitSar, s);
}
/**
 * One test for «اطلب تثبيت سعر الشحن» (owner, 2026-11-01 «وحدهم»): the product is
 * marked (staff price its shipping) OR the variant is Rule C. Server twin: the
 * confirm lines in market-checkout handler.ts (staffLines + confirmFirstLines).
 */
export function needsConfirm(info, sku, unitSar, s) {
  return staffShipping(info) || confirmFirst(sku, info?.air_class, unitSar, s);
}
/** No shipping estimate is ever shown to the customer (owner, 2026-11-02): only exact shipping — the landed price, the calculator, the cart. */

/**
 * One cart line's delivered total — the same sum the cart total is made of. With the
 * store's rounding on, that is the shown (rounded) unit × qty, so the cart equals the
 * card and the calculator (owner, 2026-11-02); without it, the parts rounded once.
 */
export function deliveredLineTotal(unitSar, sku, cls, qty, s, method) {
  if (s.price_rounding) {
    const d = deliveredUnit(unitSar, sku, cls, qty, s);
    const unit = method === 'sea' ? d.sea : d.air;
    return unit == null ? null : r2(unit * qty);
  }
  const u = unitMeasures(sku, qty, s);
  const goods = unitSar * qty;
  const ship = method === 'sea' ? r2(u.sea_cbm * seaRate(cls, s) * qty) : r2(u.air_kg * airRate(cls, s) * qty);
  return r2(goods + ship + goods * Number(s.fee_rate) + goods * allowanceRate(s));
}

/** The smallest quantity of one variant whose sea volume reaches the sea minimum (the bulk rule counts from its own quantity). */
export function seaMinQty(sku, s) {
  const min = Number(s.sea_min_cbm);
  const one = unitMeasures(sku, 1, s).sea_cbm;
  if (!(min > 0) || !(one > 0)) return 1;
  const n = Math.ceil(min / one - 1e-9);
  const bulkMin = Number(s.bulk_min_qty);
  if (n < bulkMin) return n;
  const atBulk = unitMeasures(sku, bulkMin, s);
  // from the bulk quantity the volume may shrink (bulk rule or nesting): count with it
  return atBulk.bulk || atBulk.nest ? Math.max(bulkMin, Math.ceil(min / atBulk.sea_cbm - 1e-9)) : n;
}

/** Pieces to add to one line (the one whose pieces add the most volume) until the cart reaches the sea minimum — the bulk rule counted at the new quantity. */
function piecesToSeaMin(lines, seaCbm, s) {
  const min = Number(s.sea_min_cbm);
  let best = 0;
  lines.forEach((l) => {
    const rest = seaCbm - unitMeasures(l.sku, l.qty, s).sea_cbm * l.qty;
    for (let k = 1; k <= 20000; k++) {
      if (best && k >= best) return;
      if (rest + unitMeasures(l.sku, l.qty + k, s).sea_cbm * (l.qty + k) >= min - 1e-9) { best = k; return; }
    }
  });
  return best;
}

/** A whole cart by air and by sea (lines: { unit_sar, qty, sku, cls }). Sea only from the sea minimum; add_units = pieces still missing. */
/**
 * The cart's fee base (owner, 2026-10-31: «base the tier on the delivered cart total (before
 * the tier discount)»): what the trader sees — the higher of the air and sea delivered totals
 * at the top rate, so ONE rate covers every method and sea stays the cheaper. Server twin:
 * market-checkout/delivered.ts cartFeeBase.
 */
export function cartFeeBase(c) {
  return Math.max(Number(c.air.total_sar) || 0, c.sea.available && c.sea.total_sar != null ? Number(c.sea.total_sar) : 0);
}

/** The cart, with the tiered fee: priced at the top rate to find its base, then at the base's rate. */
export function deliveredCart(lines, s) {
  const top = deliveredCartAt(lines, s, feeLadder(s)[0].rate);
  const base = cartFeeBase(top);
  const rate = cartFeeRate(base, s);
  const c = rate === feeLadder(s)[0].rate ? top : deliveredCartAt(lines, s, rate);
  // the discount the tier gives: the same in every method without rounding; with it, read on the method the cart can use
  const m = c.air.available ? 'air' : 'sea';
  const disc = top[m].total_sar != null && c[m].total_sar != null ? r2(top[m].total_sar - c[m].total_sar) : 0;
  return { ...c, fee_rate: rate, fee_base_sar: base, fee_discount_sar: disc, next_fee_tier: nextFeeTier(base, s) };
}

/**
 * The cart at one fee rate (the delivered.ts twin's deliveredCart, line for line).
 * fee_sar is everything above goods + shipping: the fee, the payment allowance and —
 * with rounding on — the rounding (each line = its shown unit × qty). Never shown as a fee.
 * Air is offered only when every line allows it (sea-only goods, §1.5).
 */
export function deliveredCartAt(lines, s, rate) {
  const sr = { ...s, fee_rate: rate };
  const fee = r2(lines.reduce((a, l) => a + l.unit_sar * l.qty * rate + l.unit_sar * l.qty * allowanceRate(s), 0));
  const goods = r2(lines.reduce((a, l) => a + l.unit_sar * l.qty, 0));
  let airKg = 0, airShip = 0, seaCbm = 0, seaShip = 0, airShown = 0, seaShown = 0;
  for (const l of lines) {
    const u = unitMeasures(l.sku, l.qty, s);
    airKg += u.air_kg * l.qty; airShip += r2(u.air_kg * airRate(l.cls, s) * l.qty);
    seaCbm += u.sea_cbm * l.qty; seaShip += r2(u.sea_cbm * seaRate(l.cls, s) * l.qty);
    if (s.price_rounding) {
      const d = deliveredUnit(l.unit_sar, l.sku, l.cls, l.qty, sr);
      airShown += d.air != null ? r2(d.air * l.qty) : 0;
      seaShown += d.sea != null ? r2(d.sea * l.qty) : 0;
    }
  }
  const airMin = airKg < Number(s.air_min_kg) && airKg > 0 ? r2((Number(s.air_min_kg) - airKg) * (airShip / airKg)) : 0;
  const airOk = lines.length > 0 && lines.every((l) => l.sku?.air_ok !== false);
  const seaOk = lines.length > 0 && lines.every((l) => l.sku?.sea_ok);
  const short = Math.max(0, Number(s.sea_min_cbm) - seaCbm);
  const seaAvailable = seaOk && short <= 1e-9;
  const addUnits = seaOk && !seaAvailable ? piecesToSeaMin(lines, seaCbm, s) : 0;
  const airTotal = s.price_rounding ? r2(airShown + airMin) : r2(goods + airShip + airMin + fee);
  const seaTotal = s.price_rounding ? r2(seaShown) : r2(goods + seaShip + fee);
  return {
    air: { available: airOk, goods_sar: goods, shipping_sar: r2(airShip + airMin), minimum_sar: airMin,
      fee_sar: s.price_rounding && airOk ? r2(airTotal - goods - airShip - airMin) : fee,
      total_sar: airOk ? airTotal : null, kg: Math.round(airKg * 1000) / 1000, air_ok: airOk },
    sea: { available: seaAvailable, goods_sar: goods, shipping_sar: r2(seaShip), minimum_sar: 0,
      fee_sar: s.price_rounding && seaAvailable ? r2(seaTotal - goods - seaShip) : fee,
      total_sar: seaAvailable ? seaTotal : null, cbm: Math.round(seaCbm * 10000) / 10000,
      sea_ok: seaOk, any_ok: lines.some((l) => l.sku?.sea_ok), add_units: addUnits },
  };
}

/** «أوفر» (sea) by default once the quantity reaches the sea minimum, «أسرع» (air) below it — sea only when offered. */
export function defaultMethod(sku, qty, s) {
  if (!sku?.sea_ok) return 'air';
  return qty >= seaMinQty(sku, s) ? 'sea' : 'air';
}

/** The cart's default: the cheaper of air and sea (sea only when offered). Takes { air, sea } totals objects. */
export function cheaperMethod(o) {
  // a cart air cannot carry (sea-only goods, 2026-11-02) defaults to sea when sea is there
  if (o?.air && o.air.available === false && o?.sea?.available) return 'sea';
  return o?.sea?.available && o.sea.total_sar != null && o?.air?.total_sar != null && Number(o.sea.total_sar) < Number(o.air.total_sar) ? 'sea' : 'air';
}

/** Arrival window as dates (local, YYYY-MM-DD) from today. */
export function arrival(method, s, today = new Date()) {
  const [a, b] = method === 'sea' ? [s.sea_days_min, s.sea_days_max] : [s.air_days_min, s.air_days_max];
  const at = (d) => { const x = new Date(today); x.setDate(x.getDate() + Number(d)); return x; };
  return { from: at(a), to: at(b) };
}

/**
 * The card's prices (owner, 2026-10-29/30). Per variant, at the minimum order:
 *   - when the minimum order ALONE reaches the sea minimum and sea is offered,
 *     the hero is the sea delivered unit price, «أسرع: X» (air) under it;
 *   - otherwise the hero is the air delivered unit price, and when sea is
 *     offered «أوفر: X بالكميات» under it — sea at its minimum quantity.
 *   - Rule B (2026-10-30): when air is unsuited to the variant (airUnsuited)
 *     and sea is offered, the hero is sea and there is NO air line — sea at
 *     the minimum order, or at the sea minimum quantity when the minimum
 *     order is below it (then seaQty says from how many pieces).
 * The card shows the variant whose hero is lowest.
 * Returns { price, method: 'air'|'sea', air, sea, seaQty } — sea/seaQty only
 * for the «أوفر بالكميات» line (air hero), air for the «أسرع» line (sea hero);
 * on a sea hero, seaQty is set only by rule B (the price is from that quantity).
 */
export function cardPrice(product, info, s, toSarFn, moq) {
  if (!info?.skus?.length) return null;
  // a product an admin marked (staff price its shipping, unified with Rule C): every
  // variant is confirm-first, with no estimate — its weight is the thing not trusted
  const marked = staffShipping(info);
  let best = null, bestConfirm = null;
  for (const v of info.skus) {
    const unitSar = toSarFn(v.price_cny);
    if (!(unitSar > 0)) continue;
    // Rule C: the product price only, shipping confirmed by staff — shown only when no variant has a delivered price
    if (marked || confirmFirst(v, info.air_class, unitSar, s)) {
      const cc = { price: productPrice(unitSar, s), method: 'confirm', air: null, sea: null, seaQty: null, est: null };
      if (!bestConfirm || cc.price < bestConfirm.price) bestConfirm = cc;
      continue;
    }
    const atMoq = deliveredUnit(unitSar, v, info.air_class, moq, s);
    const minQ = v.sea_ok ? seaMinQty(v, s) : null;
    let c;
    if (v.sea_ok && airUnsuited(v, info.air_class, unitSar, s)) {
      const at = Math.max(moq, minQ);
      c = { price: deliveredUnit(unitSar, v, info.air_class, at, s).sea, method: 'sea', air: null, sea: null, seaQty: at > moq ? at : null };
    } else if (v.sea_ok && moq >= minQ && atMoq.sea != null) c = { price: atMoq.sea, method: 'sea', air: atMoq.air, sea: null, seaQty: null };
    else {
      const z = v.sea_ok ? deliveredUnit(unitSar, v, info.air_class, Math.max(moq, minQ), s).sea : null;
      c = { price: atMoq.air, method: 'air', air: null, sea: z != null && z < atMoq.air ? z : null, seaQty: z != null && z < atMoq.air ? Math.max(moq, minQ) : null };
    }
    if (!best || c.price < best.price) best = c;
  }
  if (best) { best.low = lowestReachable(product, info, s, toSarFn, moq, best.price); return best; }
  return bestConfirm ? { ...bestConfirm, low: null } : null;
}

/**
 * The card's prices at the product's DEFAULT variant (owner, 2026-11-02): the
 * SKU the product page opens on (market_delivered.default_sku), so the card
 * and the page show the same price. The same rules as cardPrice, on that one
 * variant; every variant only when the server did not name a default (an
 * older market_delivered, or nothing in stock).
 */
export function defaultCardPrice(product, info, s, toSarFn, moq) {
  const one = info?.default_sku ? (info.skus || []).filter((v) => v.key === info.default_sku) : [];
  return cardPrice(product, one.length ? { ...info, skus: one } : info, s, toSarFn, moq);
}

/**
 * The card and the product page show the PRODUCT price only (owner, 2026-11-02, final display
 * decision): goods + fee + payment allowance, rounded — no landed price, no shipping amount, no
 * estimate. Shipping and customs appear in the cart. Per product: the lowest product price across
 * its variants (the best price, not the first variant), «يبدأ من X بالكميات» when the supplier's
 * last price break beats it by the advertising margin, and whether staff price its shipping.
 * App twin: marketDeliveredCore.js productCardPrice.
 */
export function productCardPrice(product, info, s, toSarFn) {
  if (!info?.skus?.length || !s) return null;
  let best = null;
  for (const v of info.skus) {
    const u = toSarFn(v.price_cny);
    if (!(u > 0)) continue;
    const pr = productPrice(u, s);
    if (best == null || pr < best) best = pr;
  }
  if (best == null) return null;
  const tiers = cleanTiers(product?.price_tiers);
  const last = pricingMode(product, info.skus) === 'quantity' && tiers.length ? tiers[tiers.length - 1] : null;
  const lowRaw = last ? productPrice(toSarFn(last.price_cny), s) : null;
  return { price: best, low: worthAdvertising(lowRaw, best, s) ? lowRaw : null, lowQty: last ? last.min_quantity : null, staff: staffShipping(info) };
}

/**
 * «ينزل لـ X ر.س بالكميات» (owner, 2026-10-31): the lowest per-piece price a trader can
 * reach — the supplier's last price break (when the product is priced by quantity), the
 * lowest fee tier, and sea where it is offered — at the quantity that reaches them. null
 * when nothing beats the card's own price.
 */
export function lowestReachable(product, info, s, toSarFn, moq, hero) {
  const tiers = cleanTiers(product?.price_tiers);
  const byQty = pricingMode(product, info.skus) === 'quantity' && tiers.length ? tiers[tiers.length - 1] : null;
  const sLow = { ...s, fee_rate: lowestFeeRate(s) };
  let low = null;
  for (const v of info.skus) {
    const unitSar = toSarFn(byQty ? byQty.price_cny : v.price_cny);
    if (!(unitSar > 0)) continue;
    const q = Math.max(moq, byQty ? byQty.min_quantity : 0, v.sea_ok ? seaMinQty(v, s) : 0);
    const d = deliveredUnit(unitSar, v, info.air_class, q, sLow);
    const p = d.sea != null && d.air != null ? Math.min(d.sea, d.air) : d.sea ?? d.air;
    if (p == null) continue;
    if (low == null || p < low) low = p;
  }
  return worthAdvertising(low, hero, s) ? low : null;
}

const cache = new Map();
/**
 * «تسعير الشحن بواسطة الفريق» (owner, 2026-11-01): an admin marked this product
 * (market_shipping_quote_products — weight or size not trusted). It shows its
 * price WITHOUT shipping and its checkout goes to staff pricing; the server
 * enforces the same (market-checkout staffShipping).
 */
export const staffShipping = (info) => info?.shipping_quote === true;
/** Does any product of a { [id]: info } map have its shipping priced by staff? */
export const anyStaffShipping = (map) => Object.values(map || {}).some(staffShipping);

/** market_delivered for ids, cached per page load: { [product_id]: { air_class, sea_ok, skus: [...] } }. */
export async function fetchDelivered(ids) {
  const want = [...new Set(ids)].filter((id) => !cache.has(id));
  // the function takes 100 at a time; up to four chunks in flight (the home's picks ask for ~900 —
  // ten at once drew HTTP 500s from the function on 2026-11-02)
  const chunks = [];
  for (let i = 0; i < want.length; i += 100) chunks.push(want.slice(i, i + 100));
  let next = 0;
  const one = async (chunk) => {
    let r = await marketDb.rpc('market_delivered', { p_ids: chunk });
    if (r.error) r = await marketDb.rpc('market_delivered', { p_ids: chunk });   // one quiet retry
    if (r.error) { console.warn('[delivered] market_delivered failed:', r.error.message); return; }   // not cached: asked again next time
    for (const row of Array.isArray(r.data) ? r.data : []) cache.set(row.product_id, row);
    for (const id of chunk) if (!cache.has(id)) cache.set(id, null);
  };
  await Promise.all(Array.from({ length: Math.min(4, chunks.length) }, async () => { while (next < chunks.length) await one(chunks[next++]); }));
  return Object.fromEntries(ids.map((id) => [id, cache.get(id) ?? null]));
}

// Cards ask one by one; the requests of one render are sent as one RPC.
let pending = null;
export function loadDelivered(id) {
  if (cache.has(id)) return Promise.resolve(cache.get(id));
  if (!pending) {
    pending = { ids: new Set(), promise: null };
    pending.promise = new Promise((resolve) => setTimeout(async () => {
      const batch = pending; pending = null;
      resolve(await fetchDelivered([...batch.ids]));
    }, 15));
  }
  pending.ids.add(id);
  return pending.promise.then((m) => m[id] ?? null);
}

/** The delivered data of one product, when the model is active (cfg) — else null. */
export function useDeliveredInfo(id, active) {
  const [info, setInfo] = useState(() => (active && cache.has(id) ? cache.get(id) : null));
  useEffect(() => {
    if (!active || !id) { setInfo(null); return undefined; }
    let alive = true;
    loadDelivered(id).then((v) => { if (alive) setInfo(v); });
    return () => { alive = false; };
  }, [id, active]);
  return info;
}

// Display texts (owner, 2026-10-29). The method names (air / sea) are never here: the
// method's name is shown on the checkout page only (MarketCheckout's own T).
export const DELIVERED_T = {
  // «واصل لك · شامل الشحن والجمارك» wherever a delivered price shows (owner, 2026-10-29)
  ar: { delivered: 'واصل لك', deliveredIncl: 'واصل لك · شامل الشحن والجمارك', fast: 'أسرع', cheap: 'أوفر', save: (x) => `وفّر ${x}`,
    arrives: (a, b) => `يوصل ${a} – ${b}`, included: 'شامل الشحن والجمارك', finalPrice: 'السعر نهائي، ولا ريال زيادة عند الاستلام',
    express: 'إكسبريس', expressAsk: 'مستعجل؟ إكسبريس', atCheckout: 'يُحسب عند إتمام الطلب',
    confirmLine: (h) => `نثبّت لك سعر الشحن خلال ${h} ساعة`, productOnly: 'السعر بدون الشحن',
    // owner, 2026-11-02: no approximate shipping range — these two lines instead
    confirmSub: 'بدون دفع. يوصلك السعر النهائي، وتقرر بعدها',
    confirmCart: (h) => `سلتك فيها منتج نثبّت لك سعر شحنه خلال ${h} ساعة، فالطلب كامل ينتظر التأكيد قبل الدفع. لا تدفع شيئاً الآن.`,
    confirmAllCart: (h) => `نثبّت لك سعر الشحن والجمارك لطلبك خلال ${h} ساعة، والطلب ينتظر التأكيد قبل الدفع. لا تدفع شيئاً الآن.`,
    // point 2 (owner, 2026-11-02): three options; a product with no rate asks, listed separately
    // store test 2026-11-03: the ask names its option and says why — never a bare «—»
    askFor: { sea: 'اطلب سعر الشحن البحري', air: 'اطلب سعر الشحن الجوي', express: 'اطلب سعر الإكسبريس' },
    askWhy: { staff: 'وزنه أو مقاسه غير مؤكد', express_limit: 'فوق حد الوزن أو المقاس للإكسبريس', bulky: 'منتج كبير ما يُشحن جوي',
      heavy: 'ثقيل على الشحن الجوي', refused: 'ما له سعر جاهز بهذا الخيار', no_sea: 'ما له سعر بحري جاهز', express_none: 'الإكسبريس ما أعطى سعر', none: 'يحتاج تسعير من الفريق' },
    covers: (n, t) => `يشمل ${n} من ${t} منتجات`,
    askCount: (n) => (n === 1 ? 'منتج واحد يحتاج سعر شحن' : `${n} منتجات تحتاج سعر شحن`), pricing: 'نحسب سعر الشحن…', lineAsk: 'اطلب سعر الشحن لهذا المنتج',
    askHead: 'هذه المنتجات ما لها سعر شحن جاهز بهذا الخيار. اطلب سعرها بطلب مستقل بدون دفع، ونرسل لك السعر النهائي:',
    seaShortHead: (n) => (n > 0 ? `هذه المنتجات تحتاج ${n.toLocaleString('en-US')} قطعة إضافية للشحن البحري. تبقى في السلة.` : 'هذه المنتجات ما تكفي للشحن البحري. تبقى في السلة.'),
    confirmHow: (h) => `ترسل طلبك بدون دفع، والفريق يثبّت سعر الشحن الحقيقي لمدينتك. يوصلك إشعار بالسعر النهائي، وتدفع من صفحة طلبك خلال ${h} ساعة.`,
    confirmAsk: 'اطلب سعر الشحن', confirmSent: (h) => `وصلنا طلبك. نرسل لك السعر النهائي خلال ${h} ساعة، وتدفع بعدها مرة وحدة.`,
    seaAtQty: (x) => `أوفر: ${x} بالكميات`, dropsTo: (x) => `يبدأ من ${x} بالكميات`, shipInCart: 'الشحن والجمارك تنحسب في السلة قبل الدفع', productLabel: 'سعر المنتج', productsTotal: 'المنتجات', shipCustoms: 'الشحن والجمارك',
    feeTable: 'سعر القطعة حسب قيمة سلتك', feeFrom: (n) => (Number(n) > 0 ? `سلتك من ${Number(n).toLocaleString('en-US')} ر.س` : 'أقل سلة'), feeNote: 'الخصم يتبع قيمة سلتك كاملة، لا هذا المنتج وحده.',
    feeDiscount: 'خصم الكميات', feeSaved: (x) => `وفّرت ${x}`, feeNudge: (y) => `زِد ${y} على سلتك وينزل سعر كل المنتجات`, fasterAt: (x) => `أسرع: ${x}`, unlockSave: (n, x) => `اطلب ${n} قطعة ووفّر ${x}`, moqLine: (n, y) => `أقل طلب: ${n} قطعة · ${y}`,
    perPiece: 'واصل لك، للقطعة', choose: 'اختر اللي يناسبك', fromQty: (n) => `للطلبات من ${n} قطعة`,
    totalDelivered: 'الإجمالي واصل لك', avgUnit: 'سعر القطعة واصل لك', addForSea: (n) => `أضف ${n} قطعة وتقدر تختار أوفر`, seaUnavailable: 'الشحن البحري غير متاح لبعض المنتجات في السلة',
    perUnit: 'للقطعة', total: 'الإجمالي' },
  en: { delivered: 'Delivered to you', deliveredIncl: 'Delivered · shipping & customs included', fast: 'Faster', cheap: 'Cheaper', save: (x) => `Save ${x}`,
    arrives: (a, b) => `Arrives ${a} – ${b}`, included: 'Shipping and customs included', finalPrice: 'The price is final — not a riyal more on delivery',
    express: 'Express', expressAsk: 'In a hurry? Express', atCheckout: 'Priced at checkout',
    confirmLine: (h) => `We confirm your shipping price within ${h} hours`, productOnly: 'Price without shipping',
    confirmSub: 'Nothing to pay now. You get the final price, then decide.',
    confirmCart: (h) => `Your cart has a product whose shipping price we confirm within ${h} hours, so the whole order waits for it before payment. Nothing to pay now.`,
    confirmAllCart: (h) => `We confirm the shipping and customs price of your order within ${h} hours; it waits for that before payment. Nothing to pay now.`,
    askFor: { sea: 'Request a sea shipping price', air: 'Request an air shipping price', express: 'Request an express price' },
    askWhy: { staff: 'weight or size not confirmed', express_limit: 'over the express weight or size limit', bulky: 'a large item air does not carry',
      heavy: 'too heavy for air freight', refused: 'no ready price with this option', no_sea: 'no ready sea price', express_none: 'express gave no price', none: 'priced by our team' },
    covers: (n, t) => `Covers ${n} of ${t} products`,
    askCount: (n) => (n === 1 ? '1 product needs a shipping price' : `${n} products need a shipping price`), pricing: 'Calculating shipping…', lineAsk: 'Request a shipping price for this product',
    askHead: 'These products have no ready shipping price with this option. Request their price separately — nothing to pay — and we send you the final price:',
    seaShortHead: (n) => (n > 0 ? `These products need ${n.toLocaleString('en-US')} more pieces to go by sea. They stay in your cart.` : 'These products are too few to go by sea. They stay in your cart.'),
    confirmHow: (h) => `Send your order without paying; the team confirms the real shipping price to your city. You get a notification with the final price and pay from your order page within ${h} hours.`,
    confirmAsk: 'Request a shipping price', confirmSent: (h) => `Request received. We send your final price within ${h} hours; you then pay once.`,
    seaAtQty: (x) => `Cheaper: ${x} in quantity`, dropsTo: (x) => `From ${x} in quantity`, shipInCart: 'Shipping and customs are added in the cart, before you pay', productLabel: 'Product price', productsTotal: 'Products', shipCustoms: 'Shipping and customs',
    feeTable: 'Price per piece by your cart value', feeFrom: (n) => (Number(n) > 0 ? `Cart from ${Number(n).toLocaleString('en-US')} SAR` : 'Smallest cart'), feeNote: 'The discount follows your whole cart value, not this product alone.',
    feeDiscount: 'Quantity discount', feeSaved: (x) => `You saved ${x}`, feeNudge: (y) => `Add ${y} to your cart and every product gets cheaper`, fasterAt: (x) => `Faster: ${x}`, unlockSave: (n, x) => `Order ${n} pieces and save ${x}`, moqLine: (n, y) => `Min. order: ${n} pcs · ${y}`,
    perPiece: 'Delivered to you, per piece', choose: 'Choose what suits you', fromQty: (n) => `For orders of ${n}+ pieces`,
    totalDelivered: 'Total delivered to you', avgUnit: 'Price per piece, delivered', addForSea: (n) => `Add ${n} pieces to choose Cheaper`, seaUnavailable: 'Sea shipping is not available for some products in the cart',
    perUnit: '/ piece', total: 'Total' },
  zh: { delivered: '到门价', deliveredIncl: '到门价 · 含运费和关税', fast: '更快', cheap: '更省', save: (x) => `省 ${x}`,
    arrives: (a, b) => `预计 ${a} – ${b} 送达`, included: '含运费和关税', finalPrice: '价格为最终价，收货时不再加收',
    express: '快递', expressAsk: '急用？快递', atCheckout: '结算时计算',
    confirmLine: (h) => `我们将在 ${h} 小时内为您确认运费`, productOnly: '不含运费价格',
    confirmCart: (h) => `您的购物车中有需要在 ${h} 小时内确认运费的产品，整个订单将在付款前等待确认。现在无需付款。`,
    confirmAllCart: (h) => `我们将在 ${h} 小时内确认您订单的运费和关税，订单在付款前等待确认。现在无需付款。`,
    askFor: { sea: '申请海运报价', air: '申请空运报价', express: '申请快递报价' },
    askWhy: { staff: '重量或尺寸未确认', express_limit: '超出快递重量或尺寸限制', bulky: '大件商品不走空运',
      heavy: '空运过重', refused: '此方式暂无现成价格', no_sea: '暂无现成海运价格', express_none: '快递未报价', none: '由团队报价' },
    covers: (n, t) => `包含 ${t} 件中的 ${n} 件`,
    askCount: (n) => `${n} 件商品需要报运费`, pricing: '正在计算运费…', lineAsk: '为此商品申请运费报价',
    askHead: '以下商品在此方式下暂无运费。可单独申请报价（无需付款），我们会发送最终价格：',
    seaShortHead: (n) => (n > 0 ? `以下商品还需 ${n.toLocaleString('en-US')} 件才能海运，仍保留在购物车中。` : '以下商品数量不足以海运，仍保留在购物车中。'),
    confirmHow: (h) => `提交订单无需付款，团队将确认到您所在城市的实际运费。您会收到最终价格通知，并在 ${h} 小时内于订单页面付款。`,
    confirmAsk: '申请确认运费', confirmSent: (h) => `已收到您的请求。我们将在 ${h} 小时内发送最终价格，届时一次性付款。`,
    seaAtQty: (x) => `更省：批量 ${x}`, dropsTo: (x) => `批量 ${x} 起`, shipInCart: '运费和关税在购物车中付款前计算', productLabel: '产品价格', productsTotal: '产品', shipCustoms: '运费和关税',
    feeTable: '按购物车金额的单价', feeFrom: (n) => (Number(n) > 0 ? `购物车满 ${Number(n).toLocaleString('en-US')} 里亚尔` : '最小购物车'), feeNote: '折扣按整个购物车金额计算，而非单个产品。',
    feeDiscount: '批量折扣', feeSaved: (x) => `已省 ${x}`, feeNudge: (y) => `再加 ${y}，所有产品单价都会下降`, fasterAt: (x) => `更快：${x}`, unlockSave: (n, x) => `订购 ${n} 件，省 ${x}`, moqLine: (n, y) => `起订 ${n} 件 · ${y}`,
    perPiece: '到门价（每件）', choose: '选择适合您的方式', fromQty: (n) => `${n} 件起`,
    totalDelivered: '到门总价', avgUnit: '每件到门价', addForSea: (n) => `再加 ${n} 件即可选择更省`, seaUnavailable: '购物车中部分商品不支持海运',
    perUnit: '/ 件', total: '合计' },
};

/** A date the way the page reads: 16 Nov / ١٦ نوفمبر. */
export const shortDate = (d, lang) => d.toLocaleDateString(lang === 'ar' ? 'ar-SA-u-ca-gregory-nu-latn' : lang === 'zh' ? 'zh-CN' : 'en-GB', { day: 'numeric', month: 'short' });

/** The delivered data of several products (the cart) — { [id]: info|null }, {} until loaded or when inactive. */
export function useDeliveredMany(ids, active) {
  const key = [...new Set(ids)].sort().join(',');
  const [map, setMap] = useState({});
  useEffect(() => {
    if (!active || !key) { setMap({}); return undefined; }
    let alive = true;
    fetchDelivered(key.split(',')).then((m) => { if (alive) setMap(m); });
    return () => { alive = false; };
  }, [key, active]);
  return map;
}

/** Cart lines priced by the delivered model: null when any line has no data yet. */
export function deliveredCartFor(priced, map, s) {
  const lines = [];
  for (const g of priced?.groups || []) for (const r of g.lines) {
    if (!r.valid) continue;
    const info = map[g.product_id];
    const sku = info?.skus?.find((v) => v.key === (r.line.sku_key ?? ''));
    if (!info || !sku) return null;
    // a product whose shipping staff price: no delivered total for this cart (market_staff_shipping)
    if (info.shipping_quote) return null;
    // an Express-only product (the partner refuses it — §4.1): no partner air/sea; HioBuy Express prices it at checkout
    if (info.express_only) return null;
    lines.push({ unit_sar: r.quote.unitSar, qty: r.line.qty, sku, cls: info.air_class });
  }
  return lines.length ? deliveredCart(lines, s) : null;
}
